<template>
  <div class="flex flex-col gap-9 max-w-[360px] md:max-w-[390px] mx-auto">
    <div v-if="!isRaw" class="text-center">
      <PageHeaderLogo />
    </div>

    <PreLoginData
      v-if="status === 'PreLogin'"
      v-model:data="data"
      v-model:status="status"
      @next="preLoginNextClick"
      @signed-in="onSignedInNextClick"
    />

    <LoginData
      v-if="status === 'SignIn'"
      v-model:data="data"
      v-model:preferences="signInPreferences"
      @back="onSignInBackClick"
      @next="onSignedInNextClick"
    />

    <SuccessLoginData v-if="status === 'Success'" :redirect-to="redirectToAfterSignIn" />
  </div>
</template>

<script setup lang="ts">
import { useGlobalConfigStore } from "@/features/Shared";
import type { SignInPreferencesType, SignInStatusType, SignInType } from "@authentication/types";
import PageHeaderLogo from "@authentication/ui/atoms/PageHeaderLogo/PageHeaderLogo.vue";
import LoginData from "@authentication/ui/organisms/LoginData/LoginData.vue";
import PreLoginData from "@authentication/ui/organisms/PreLoginData/PreLoginData.vue";
import SuccessLoginData from "@authentication/ui/organisms/SuccessLoginData/SuccessLoginData.vue";
import { useUiStore } from "@common/stores";
import { useHeadSafe } from "@unhead/vue";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";

// Variables
const { t } = useTranslation();
const { realm } = storeToRefs(useUiStore());
const { isRaw } = useGlobalConfigStore();
const route = useRoute();
const data = ref<SignInType>({
  username: "",
  password: "",
  rememberMe: false,
  redirectUri: "",
});
const signInPreferences = ref<SignInPreferencesType>({
  eOtpEnabled: false,
  sOtpEnabled: false,
  tOtpEnabled: false,
  passkeyEnabled: false,
  preferred2FaMethodType: "Auto",
});
const redirectToAfterSignIn = ref("");
const status = ref<SignInStatusType>("PreLogin");

// Strings
const pageTitleString = t("login.page_title", { realmName: realm.value.name });

// Methods
const onSignInBackClick = () => {
  data.value.username = "";
  data.value.password = "";
  data.value.rememberMe = false;

  status.value = "PreLogin";
};

const preLoginNextClick = (preferences: SignInPreferencesType) => {
  signInPreferences.value = preferences;
  status.value = "SignIn";
};

const onSignedInNextClick = (redirectUrl: string) => {
  redirectToAfterSignIn.value = redirectUrl;
  status.value = "Success";
};

// Hooks
onBeforeMount(() => {
  data.value.redirectUri = route.query.redirect_uri?.toString() ?? "";
});

useHeadSafe({
  title: pageTitleString,
});
</script>
